import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Btn from '../Btn'

import styles from './style.module.scss'

/**
 * Den Aktivierungslink einer Einladung selbst weitergeben.
 *
 * Die Einladungsmail kommt von einer Adresse, die die eingeladene Person nicht
 * kennt, und landet deshalb manchmal im Spam. Kommt derselbe Link von der
 * Lehrkraft, der Schülerin oder der Ansprechperson bei Junior Achievement,
 * die die Person kennt, kommt er an. Zwei Wege: kopieren (für Messenger oder
 * eine eigene Mail) und das eigene Mailprogramm mit vorbereitetem Text öffnen.
 *
 * Der Link ist so viel wert wie ein Zugang - mit ihm legt man das Profil an.
 * Deshalb steht er nur hier und nicht in der Liste.
 */
export default function InvitationLinkShare({
	link,
	email,
	recipientName,
	companyName,
	senderName,
	validDays = 60
}) {
	const { t } = useTranslation()
	const [copied, setCopied] = useState(false)

	if (!link) return null

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(link)
			setCopied(true)
			setTimeout(() => setCopied(false), 2500)
		} catch {
			// Ohne Freigabe für die Zwischenablage (oder ohne HTTPS) bleibt das
			// Feld zum Markieren - deshalb ist es ein Eingabefeld und kein Text.
			setCopied(false)
		}
	}

	const subject = companyName
		? t('inviteShareSubjectCompany', { company: companyName })
		: t('inviteShareSubject')
	const body = [
		recipientName ? t('inviteShareGreeting', { name: recipientName }) : t('inviteShareGreetingPlain'),
		'',
		companyName ? t('inviteShareBodyCompany', { company: companyName }) : t('inviteShareBody'),
		'',
		link,
		'',
		t('inviteShareBodyValidity', { days: validDays }),
		'',
		senderName ? t('inviteShareClosing', { name: senderName }) : t('inviteShareClosingPlain')
	].join('\n')
	const mailto = `mailto:${encodeURIComponent(email || '')}?subject=${encodeURIComponent(
		subject
	)}&body=${encodeURIComponent(body)}`

	return (
		<div className={styles.root}>
			<span className={styles.label}>{t('inviteShareTitle')}</span>
			<p className={styles.hint}>{t('inviteShareHint')}</p>
			<div className={styles.row}>
				<input
					className={styles.link}
					value={link}
					readOnly
					aria-label={t('inviteShareTitle')}
					onFocus={event => event.target.select()}
				/>
				<Btn type="button" theme="secondary" onClick={copy}>
					{copied ? t('inviteShareCopied') : t('inviteShareCopy')}
				</Btn>
				<a className={styles.mailto} href={mailto}>
					{t('inviteShareMail')}
				</a>
			</div>
		</div>
	)
}

InvitationLinkShare.propTypes = {
	link: PropTypes.string,
	email: PropTypes.string,
	recipientName: PropTypes.string,
	companyName: PropTypes.string,
	senderName: PropTypes.string,
	validDays: PropTypes.number
}
